import { useContext, useEffect, useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Breadcrumbs, Tabs, Tab, Typography, Box, FormControl, Select, MenuItem, InputLabel, FormHelperText, Modal, TextField } from '@mui/material';
import { MainContainer, Container } from '../../../../components';
import { Menu } from '../Menu/Menu';
import blobToJson from '../../../../utils/blobToJson';

import { downloadFeedbackReport, getDataForScoreManagement } from '../../../../services/masterService';

import ARROW from '../../../../assets/images/breadcrumb-arrow.svg';
import ARROW_OPEN from '../../../../assets/images/breadcrumb-arrow-open.svg';
import RadiusButton from '../../../../components/RadiusButton';
import ATTENDANCE_DATA from '../../../../assets/images/attendance-data.svg';
import QUESTION_TEMPLATE from '../../../../assets/images/question.svg';
import ANSWER_KEY from '../../../../assets/images/answer-key.svg';
import RAW_SCORES from '../../../../assets/images/raw-scores.svg';
import REPORT from '../../../../assets/images/report.svg';
import DOWNLOAD from '../../../../assets/images/download.svg';
import UPLOAD from '../../../../assets/images/upload-green.svg';
import GENERATE from '../../../../assets/images/generate.svg';

import dayjs from '../../../../config/dayjs';
import { downloadAnswerPaperByRollNos, exportAnswerPaperByRollNos, downloadAttendanceSheet, downloadAuditLogReportByRollNo, getPresentCount } from '../../../../services/candidateService';
import { useToaster } from '../../../../providers/ToasterProvider';
import { LoadingButton } from '@mui/lab';
import { downloadQuestionTemplate } from '../../../../services/questionService';
import { UpdateAnswerKeyModal } from '../UpdateAnswerKeyModal/UpdateAnswerKeyModal';
import { downloadRawScore, downloadTenXReport, generateRawScore } from '../../../../services/rawScoreService';
import { ProjectContext } from '../../../../providers/projectProvider';

let timerId;

function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ px: 4, pt: 2, pb: 4 }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `tab-${index}`,
        'aria-controls': `tabpanel-${index}`,
    };
}

export const ScoreManagement = () => {

    const { addNotification } = useToaster();
    const { projectDetails } = useContext(ProjectContext);
    const defaultDrive = { id: 0, driveName: 'None' };
    const [drives, setDrives] = useState([defaultDrive]);
    const [selectedDrive, setSelectedDrive] = useState(0);
    const defaultCourse = { id: 0, courseName: 'None' };
    const [allCourses, setAllCourses] = useState([]);
    const [courses, setCourses] = useState([defaultCourse]);
    const [selectedCourse, setSelectedCourse] = useState(0);
    const defaultBatch = { id: 0, batchName: 'None' };
    const [allBatches, setAllBatches] = useState([]);
    const [batches, setBatches] = useState([defaultBatch]);
    const [selectedBatch, setSelectedBatch] = useState(0);
    const defaultProjectMaster = { id: 0, driveId: 0, courseId: 0, batchId: 0, displayExamDate: 'None' };
    const [allProjectMasters, setAllProjectMasters] = useState([defaultProjectMaster]);
    const [selectedProjectMaster, setSelectedProjectMaster] = useState(0);
    const [projectMasters, setProjectMasters] = useState([defaultProjectMaster]);
    const [currentState, setCurrentState] = useState({
        drive: 0,
        course: 0,
        batch: 0,
        projectMaster: 0,
    })
    const [candidateCounts, setCandidateCounts] = useState({
        total: 0,
        present: 0,
        absent: 0,
    });

    const [isDownloadingAttendanceData, setIsDownloadingAttendanceData] = useState(false);
    const [isDownloadingQuestionTemplate, setIsDownloadingQuestionTemplate] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [examType, setExamType] = useState(null);
    const [isGenerated, setIsGenerated] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isDownloadingReport, setIsDownloadingReport] = useState(false);
    const [isDownloadingFeedbackReport, setIsDownloadingFeedbackReport] = useState(false);
    const [isDownloadingAuditLog, setIsDownloadingAuditLog] = useState(false);
    const [auditLogModalVisible, setAuditLogModalVisible] = useState(false);
    const [answerPaperDownloadPopup, setAnswerPaperDownloadPopup] = useState({
        isOpen: false,
        selectedTab: 0,
    });
    const [answerPaperDownloading, setAnswerPaperDownloading] = useState(false);

    const scoreManagementSchema = z.object({
        drive: z.number().gte(1),
        course: z.number().gte(1),
        batch: z.number().gte(1),
        examDate: z.number().gte(1),
    });
    const rollNoSchema = z.object({
        rollNo: z.string().min(1, { message: 'Roll no is required.' }),
        drivePath: z.string().optional(),
    }).superRefine(({ drivePath }, ctx) => {
        if (answerPaperDownloadPopup.selectedTab === 1 && !drivePath) {
            ctx.addIssue({
                code: 'custom',
                message: 'Drive path is required.',
                path: ['drivePath']
            });
        }
    });


    const {
        formState: { errors },
        trigger,
        setValue,
    } = useForm({
        resolver: zodResolver(scoreManagementSchema),
    });
    const {
        register,
        handleSubmit,
        formState: { errors: rollNoErrors },
        trigger: rollNoTrigger,
        setValue: rollNoSetValue,
        reset
    } = useForm({
        resolver: zodResolver(rollNoSchema),
    });
    const {
        register: answerPaperDownloadRegister,
        handleSubmit: answerPaperDownloadHandleSubmit,
        formState: { errors: answerPaperDownloadErrors },
        trigger: answerPaperDownloadTrigger,
        reset: answerPaperDownloadReset,
    } = useForm({
        resolver: zodResolver(rollNoSchema),
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await getDataForScoreManagement();
                if (response.code === 'OK') {
                    setDrives([defaultDrive, ...response.data.drives]);
                    setAllCourses([...response.data.courses]);
                    // setCourses([defaultCourse, ...response.data.courses]);
                    setAllBatches([...response.data.batches]);
                    // setBatches([defaultBatch, ...response.data.batches]);
                    setAllProjectMasters(response.data.projectMasters.map(pm => {
                        pm.displayExamDate = dayjs(pm.examDate).format('DD-MM-YYYY')
                        return pm;
                    }));
                }
            } catch (error) {
                console.log('There is an error while fetching update time required data', error);
            }
        }
        fetchData();
    }, []);

    useEffect(() => {
        let filteredCourses = [defaultCourse];
        if (selectedDrive) {
            for (let projectMaster of allProjectMasters) {
                if (projectMaster.driveId === selectedDrive) {
                    const course = allCourses.find(course => course.id === projectMaster.courseId);
                    if (course && !filteredCourses.find(c => c.id === course.id)) {
                        filteredCourses.push(course);
                    }
                }
            }
        }
        setCourses(filteredCourses);
        setSelectedCourse(0);
        setValue('course', 0);
        setBatches([defaultBatch]);
        setSelectedBatch(0);
        setValue('batch', 0);
        setProjectMasters([defaultProjectMaster]);
        setSelectedProjectMaster(0);
        setValue('examDate', 0);
        setExamType(null);
        setCandidateCounts({
            total: 0,
            present: 0,
            absent: 0,
        })
    }, [selectedDrive]);

    useEffect(() => {
        let filteredBatches = [defaultBatch];
        if (selectedDrive && selectedCourse) {
            for (let projectMaster of allProjectMasters) {
                if (projectMaster.driveId === selectedDrive && projectMaster.courseId === selectedCourse) {
                    const batch = allBatches.find(batch => batch.id === projectMaster.batchId);
                    if (batch && !filteredBatches.find(b => b.id === batch.id)) {
                        filteredBatches.push(batch);
                    }
                }
            }
        }
        setBatches(filteredBatches);
        setSelectedBatch(0);
        setValue('batch', 0);
        setProjectMasters([defaultProjectMaster]);
        setSelectedProjectMaster(0);
        setValue('examDate', 0);
        setExamType(null);
        setCandidateCounts({
            total: 0,
            present: 0,
            absent: 0,
        })
    }, [selectedCourse]);

    useEffect(() => {
        const filteredProjectMasters = [defaultProjectMaster];
        if (selectedDrive && selectedCourse && selectedBatch) {
            for (let projectMaster of allProjectMasters) {
                if (projectMaster.driveId === selectedDrive && projectMaster.courseId === selectedCourse && projectMaster.batchId === selectedBatch) {
                    if (!filteredProjectMasters.find(pm => pm.id === projectMaster.id)) {
                        filteredProjectMasters.push(projectMaster);
                    }
                }
            }
        }
        setProjectMasters(filteredProjectMasters);
        setSelectedProjectMaster(0);
        setValue('examDate', 0);
        setExamType(null);
        setCandidateCounts({
            total: 0,
            present: 0,
            absent: 0,
        })
    }, [selectedBatch]);

    const handleSelectProjectMaster = async () => {
        if (!selectedProjectMaster) return;
        try {
            const res = await getPresentCount(selectedProjectMaster);
            if (res.code === 'OK') {
                setCurrentState({
                    drive: selectedDrive,
                    course: selectedCourse,
                    batch: selectedBatch,
                    projectMaster: selectedProjectMaster
                });
                setExamType(res.data.examType);
                setIsGenerated(res.data.isGenerated);
                if (res.data.isGenerated && timerId) {
                    clearInterval(timerId);
                }
                setIsGenerating(res.data.isGeneratingScores);
                setCandidateCounts({
                    total: res.data.totalCount,
                    present: res.data.presentCount,
                    absent: res.data.absentCount
                })
            }
        } catch (error) {
            const code = error?.response?.data?.code;
            if (code === 'EXAM_NOT_ENDED') {
                addNotification('Exam not ended.', 'information');
            }
            if (code === 'EXAM_NOT_STARTED') {
                addNotification('Exam has not yet Started.', 'information');
            }
            console.log('There is an error while getting present count', error);
        }
    }

    const handleDownloadAttendanceData = async () => {
        if (!currentState.projectMaster) {
            addNotification('Please select exam for further processing');
            return;
        }
        setIsDownloadingAttendanceData(true);
        try {
            const res = await downloadAttendanceSheet(currentState.projectMaster);
            const url = window.URL.createObjectURL(res);

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
                'download',
                `attendance-sheet.xlsx`,
            );
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            addNotification('File downloaded successfully.', 'success');

        } catch (error) {
            const code = error?.response?.data?.code;
            if (code === 'E_INTERNAL_SERVER_ERROR') {
                addNotification('Attendance sheet does not generated.');
            }
            console.log('There is an error while downloading attendance data', error);
        } finally {
            setIsDownloadingAttendanceData(false);
        }
    }

    const handleDownloadQuestionTemplate = async () => {
        if (!currentState.projectMaster) {
            addNotification('Please select exam for further processing');
            return;
        }
        if (examType !== 'MCQ') {
            addNotification('Exam type is paperless or typing can not download question template.');
            return;
        }
        setIsDownloadingQuestionTemplate(true);
        try {
            const res = await downloadQuestionTemplate(currentState.projectMaster);
            const url = window.URL.createObjectURL(res);

            const link = document.createElement('a');
            link.href = url;
            const course = courses.find(course => course.id === selectedCourse);
            const batch = batches.find(b => b.id === selectedBatch);
            const projectMaster = allProjectMasters.find(pm => pm.id === selectedProjectMaster);
            link.setAttribute(
                'download',
                `${course?.courseName}_${batch?.batchName}_${projectMaster?.displayExamDate}.xlsx`,
            );
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            addNotification('File downloaded successfully.', 'success');

        } catch (error) {
            const code = error?.response?.data?.code;
            if (code === 'E_INTERNAL_SERVER_ERROR') {
                addNotification('Attendance sheet does not generated.');
            } else if (code === 'EXAM_TYPE_INVALID') {
                addNotification('Exam type is paperless or typing can not download question template.');
            }
            console.log('There is an error while downloading attendance data', error);
        } finally {
            setIsDownloadingQuestionTemplate(false);
        }
    }

    const handleGenerateRawScore = async () => {
        if (!currentState.projectMaster) {
            addNotification('Please select exam for further processing');
            return;
        }
        if (examType !== 'MCQ') {
            addNotification('Exam type is paperless or typing can not generate raw scores.');
            return;
        }
        try {
            await generateRawScore(currentState.projectMaster);
            addNotification('Raw scores generation started.', 'success');
            setIsGenerating(true);

            // setting interval to check whether the raw scores are generated or not
            timerId = setInterval(() => {
                handleSelectProjectMaster();
            }, 5000);
        } catch (error) {
            const code = error?.response?.data?.code;
            if (code === 'EXAM_TYPE_INVALID') {
                addNotification('Exam type is paperless or typing can not generate raw scores.');
            } else if (code === 'EXAM_NOT_ENDED') {
                addNotification('Exam not ended yet.');
            } else if (code === 'UNPROCESSABLE_ENTITY') {
                addNotification('Score generation is in progress.', 'information')
            }
            console.log('There is an error while generating raw scores', error);
        }
    }

    const handleDownloadRawScore = async () => {
        if (!currentState.projectMaster) {
            addNotification('Please select exam for further processing');
            return;
        }
        if (examType !== 'MCQ') {
            addNotification('Exam type is paperless or typing can not download raw scores.');
            return;
        }
        setIsDownloading(true);
        try {
            const res = await downloadRawScore(currentState.projectMaster);
            const url = window.URL.createObjectURL(res);

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
                'download',
                `scores.xlsx`,
            );
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            addNotification('File downloaded successfully.', 'success');
        } catch (error) {
            const data = error?.response?.data;
            if (data) {
                const result = await blobToJson(data);
                if (result?.code === 'SCORES_ARE_NOT_GENERATED') {
                    addNotification('Scores are not generated, please generate to proceed.', 'danger');
                } else if (result?.code === 'EXAM_TYPE_INVALID') {
                    addNotification('Exam type is paperless or typing can not download raw scores.', 'information');
                } else if (result?.code === 'GENERATING_SCORES') {
                    addNotification('Generating scores please wait.', 'danger');
                } else {
                    addNotification('Error while downloading.', 'danger');
                }
            }
            console.log('There is an error while downloading raw scores', error);
        } finally {
            setIsDownloading(false);
        }
    }

    const handleDownloadReport = async () => {
        if (!currentState.projectMaster) {
            addNotification('Please select exam for further processing');
            return;
        }
        if (examType !== 'MCQ') {
            addNotification('Exam type is paperless or typing can not download ten x report.');
            return;
        }
        setIsDownloadingReport(true);
        try {
            const res = await downloadTenXReport(currentState.projectMaster);
            const url = window.URL.createObjectURL(res);

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
                'download',
                `10X_Report.xlsx`,
            );
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            addNotification('File downloaded successfully.', 'success');
        } catch (error) {
            const data = error?.response?.data;
            if (data) {
                const result = await blobToJson(data);
                if (result?.code === 'SCORES_ARE_NOT_GENERATED') {
                    addNotification('Scores are not generated, please generate to proceed.', 'danger');
                } else if (result?.code === 'EXAM_TYPE_INVALID') {
                    addNotification('Exam type is paperless or typing can not download ten x report.');
                } else if (result?.code === 'GENERATING_SCORES') {
                    addNotification('Generating scores please wait.', 'danger');
                } else {
                    addNotification('Error while downloading.', 'danger');
                }
            }
            console.log('There is an error while downloading ten x report', error);
        } finally {
            setIsDownloadingReport(false);
        }
    }

    const handleOpenRollNoPopup = async () => {
        if (!currentState.projectMaster) {
            addNotification('Please select exam for further processing');
            return;
        }
        setAuditLogModalVisible(true);
    }
    const handleCloseRollNoPopup = () => {
        setAuditLogModalVisible(false);
        reset();
    }

    const openAnswerPaperDownloadPopup = async () => {
        if (!currentState.projectMaster) {
            addNotification('Please select exam for further processing');
            return;
        }
        setAnswerPaperDownloadPopup({
            isOpen: true,
            selectedTab: 0,
        });
    }
    const closeAnswerPaperDownloadPopup = () => {
        setAnswerPaperDownloadPopup({
            isOpen: false,
            selectedTab: 0,
        });
        answerPaperDownloadReset();
    }

    const answerPaperDownloadSubmit = async (value) => {
        if (!currentState.projectMaster) {
            addNotification('Please select exam for further processing');
            return;
        }
        const rollNos = value.rollNo.split(',').map((value) => value.trim());
        setAnswerPaperDownloading(true);
        try {
            if (answerPaperDownloadPopup.selectedTab === 0) {
                const body = {
                    rollNos: rollNos,
                    projectMasterId: currentState.projectMaster
                }
                const res = await downloadAnswerPaperByRollNos(body);
                const url = window.URL.createObjectURL(res);

                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'answer-pages.zip');
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                addNotification('File downloaded successfully.', 'success');
                setAnswerPaperDownloadPopup({
                    isOpen: false,
                    selectedTab: 0,
                });
                answerPaperDownloadReset();
            } else if (answerPaperDownloadPopup.selectedTab === 1) {
                const body = {
                    projectMasterId: currentState.projectMaster,
                    rollNos: rollNos,
                    drivePath: value.drivePath
                }
                const res = await exportAnswerPaperByRollNos(body);
                addNotification('Answers exported successfully.', 'success');
                setAnswerPaperDownloadPopup({
                    isOpen: false,
                    selectedTab: 0,
                });
                answerPaperDownloadReset();
            }
        } catch (error) {
            const data = error?.response?.data;
            if (data) {
                const result = await blobToJson(data);
                if (result?.code === 'ALL_ROLL_NO_NOT_VALID') {
                    addNotification('Some candidate roll no is invalid.', 'danger');
                } else if (result?.code === 'EXAM_TYPE_INVALID') {
                    addNotification('Exam type is not paperless can not download answer paper.', 'danger');
                } else {
                    addNotification('Error while downloading.', 'danger');
                }
            }
            console.log('There is an error while downloading answer paper.', error);
        } finally {
            setAnswerPaperDownloading(false);
        }
    }

    const handleDownloadAuditLog = async ({ rollNo }) => {
        let refinedRollNo = rollNo?.trim();
        if (!refinedRollNo) return;

        let projectMasterId = currentState.projectMaster;
        if (!projectMasterId) {
            addNotification('Please select exam for further processing');
            return;
        }

        setIsDownloadingAuditLog(true);
        try {
            const res = await downloadAuditLogReportByRollNo(projectMasterId, refinedRollNo);
            const url = window.URL.createObjectURL(res);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
                'download',
                `Audit Logs.csv`,
            );
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            addNotification('Audit logs downloaded successfully.', 'success');
            handleCloseRollNoPopup();
        } catch (error) {
            const data = error?.response?.data;
            if (data) {
                const result = await blobToJson(data);
                if (result?.code === 'E_NOT_FOUND') {
                    addNotification('Requested candidate not found.', 'danger');
                } else if (result?.code === 'EXAM_NOT_ATTENDED') {
                    addNotification('Candidate does not attended the exam.', 'danger');
                } else {
                    addNotification('Error while downloading.', 'danger');
                }
            }
            console.log('There is an error while downloading audit log report', error);
        } finally {
            setIsDownloadingAuditLog(false);
        }
    }

    const handleDownloadFeedbackReport = async () => {
        if (!currentState.projectMaster) {
            addNotification('Please select exam for further processing');
            return;
        }
        setIsDownloadingFeedbackReport(true);
        try {
            const res = await downloadFeedbackReport(currentState.projectMaster);
            const url = window.URL.createObjectURL(res);

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
                'download',
                `Feedback_Report.csv`,
            );
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            addNotification('File downloaded successfully.', 'success');
        } catch (error) {
            const data = error?.response?.data;
            if (data) {
                const result = await blobToJson(data);
                if (result?.code === 'FEEDBACK_NOT_ENABLED') {
                    addNotification('Feedback not enabled for this exam.', 'danger');
                } else if (result?.code === 'EXAM_NOT_ENDED') {
                    addNotification('Exam not ended.', 'danger');
                } else {
                    addNotification('Error while downloading.', 'danger');
                }
            }
            console.log('There is an error while downloading ten x report', error);
        } finally {
            setIsDownloadingFeedbackReport(false);
        }
    }

    return (
        <MainContainer>
            <Menu>
                <Breadcrumbs aria-label="breadcrumb" separator='' sx={{ gap: 12 }}>
                    <Typography color="text.primary" sx={{ fontWeight: 400, textTransform: 'capitalize' }}>{projectDetails?.projectName}</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW} alt=''></img>
                    </Box>
                    <Typography color="text.primary" sx={{ fontWeight: 500 }}>
                        Score Management
                    </Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW_OPEN} alt='' ></img>
                    </Box>
                </Breadcrumbs>
            </Menu>

            <Container>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    <Typography component='h5' variant='h5' sx={{ mb: 3 }}>Score Management</Typography>
                    <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'flex-start',
                        marginBottom: 3,
                        columnGap: 2,
                        rowGap: 1
                    }}>
                        <FormControl className='form-field' sx={{ width: 200 }}>
                            <InputLabel id="driveName">Drive Name</InputLabel>
                            <Select
                                size='small'
                                labelId="driveName"
                                id="drive"
                                label='Drive Name'
                                name='drive'
                                error={Boolean(errors.drive)}
                                onBlur={() => trigger('drive')}
                                value={selectedDrive ?? 0}
                                onChange={(e, event) => {
                                    const drive = drives.find((d) => d.id === event.props.value);
                                    setSelectedDrive(drive.id);
                                    setValue('drive', event.props.value);
                                }}
                            >
                                {
                                    drives.map(
                                        (drive) => <MenuItem key={drive.id} value={drive.id}>
                                            {drive.driveName}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {errors.drive?.message
                                && <FormHelperText sx={{ color: 'error.main' }}>Please select drive name.</FormHelperText>
                            }
                        </FormControl>
                        <FormControl className='form-field' sx={{ width: 200 }}>
                            <InputLabel id="courseName">Course Name</InputLabel>
                            <Select
                                size='small'
                                labelId="courseName"
                                id="course"
                                label='Course Name'
                                name='course'
                                error={Boolean(errors.course)}
                                onBlur={() => trigger('course')}
                                value={selectedCourse ?? 0}
                                onChange={(e, event) => {
                                    const course = courses.find((d) => d.id === event.props.value);
                                    setSelectedCourse(course.id);
                                    setValue('course', event.props.value);
                                }}
                            >
                                {
                                    courses.map(
                                        (course) => <MenuItem key={course.id} value={course.id}>
                                            {course.courseName}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {errors.course?.message
                                && <FormHelperText sx={{ color: 'error.main' }}>Please select course name.</FormHelperText>
                            }
                        </FormControl>
                        <FormControl className='form-field' sx={{ width: 200 }}>
                            <InputLabel id="batchName">Batch Name</InputLabel>
                            <Select
                                size='small'
                                labelId="batchName"
                                id="batch"
                                label='Batch Name'
                                name='batch'
                                error={Boolean(errors.batch)}
                                onBlur={() => trigger('batch')}
                                value={selectedBatch ?? 0}
                                onChange={(e, event) => {
                                    const batch = batches.find((b) => b.id === event.props.value);
                                    setSelectedBatch(batch.id);
                                    setValue('batch', event.props.value);
                                }}
                            >
                                {
                                    batches.map(
                                        (batch) => <MenuItem key={batch.id} value={batch.id}>
                                            {batch.batchName}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {errors.batch?.message
                                && <FormHelperText sx={{ color: 'error.main' }}>Please select batch name.</FormHelperText>
                            }
                        </FormControl>
                        <FormControl className='form-field' sx={{ width: 200 }}>
                            <InputLabel id="examDateLabel">Exam Date</InputLabel>
                            <Select
                                size='small'
                                labelId="examDateLabel"
                                id="examDate"
                                label='Exam Date'
                                name='examDate'
                                error={Boolean(errors.examDate)}
                                onBlur={() => trigger('examDate')}
                                value={selectedProjectMaster ?? 0}
                                onChange={(e, event) => {
                                    const projectMaster = projectMasters.find((pm) => pm.id === event.props.value);
                                    setSelectedProjectMaster(projectMaster.id);
                                    setValue('examDate', event.props.value);
                                }}
                            >
                                {
                                    projectMasters.map(
                                        (pm) => <MenuItem key={pm.id} value={pm.id}>
                                            {pm.displayExamDate}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {errors.examDate?.message
                                && <FormHelperText sx={{ color: 'error.main' }}>Please select exam date.</FormHelperText>
                            }
                        </FormControl>

                        <RadiusButton sx={{
                            textTransform: 'uppercase',
                            fontWeight: 500,
                            ml: 'auto'
                        }} color='primary'
                            variant='contained'
                            size='large'
                            onClick={handleSelectProjectMaster}
                            disabled={!selectedDrive || !selectedCourse || !selectedBatch || !selectedProjectMaster}
                        >
                            select
                        </RadiusButton>
                        <RadiusButton sx={{
                            textTransform: 'uppercase',
                            fontWeight: 500,
                        }} color='primary'
                            variant='contained'
                            size='large'
                            onClick={handleSelectProjectMaster}
                            disabled={!selectedDrive || !selectedCourse || !selectedBatch || !selectedProjectMaster}
                        >
                            Refresh
                        </RadiusButton>
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: 'background.base',
                        p: 3
                    }}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 2,
                            mb: 3,
                        }}>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                rowGap: 1,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'grey.500',
                                height: 130,
                                width: '30%',
                                maxWidth: 305,
                                p: 3,
                                textAlign: 'center',
                            }}>
                                <Typography sx={{
                                    color: 'primary.main',
                                    fontSize: 18,
                                    fontWeight: 500,
                                }}>{candidateCounts.total}</Typography>
                                <Typography sx={{
                                    fontWeight: 500,
                                }}>
                                    Candidate Schedule
                                </Typography>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                rowGap: 1,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'grey.500',
                                height: 130,
                                width: '30%',
                                maxWidth: 305,
                                p: 3,
                                textAlign: 'center',
                            }}>
                                <Typography sx={{
                                    color: 'success.main',
                                    fontSize: 18,
                                    fontWeight: 500,
                                }}>{candidateCounts.present}</Typography>
                                <Typography sx={{
                                    fontWeight: 500,
                                }}>
                                    Candidate Present
                                </Typography>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                rowGap: 1,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'grey.500',
                                height: 130,
                                width: '30%',
                                maxWidth: 305,
                                p: 3,
                                textAlign: 'center',
                            }}>
                                <Typography sx={{
                                    color: 'error.main',
                                    fontSize: 18,
                                    fontWeight: 500,
                                }}>{candidateCounts.absent}</Typography>
                                <Typography sx={{
                                    fontWeight: 500,
                                }}>
                                    Candidate Absent
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                            maxWidth: 1040,
                            margin: '0 auto',
                            columnGap: 4,
                            rowGap: 4,
                        }}>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'space-evenly',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'grey.500',
                                height: 210,
                                width: 310
                            }}>
                                <Box sx={{
                                    width: 60,
                                    height: 60,
                                    background: '#E1E8F5',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignContent: 'center'
                                }}>
                                    <img src={ATTENDANCE_DATA} alt="" />
                                </Box>
                                <Box sx={{
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                }}>attendance data</Box>
                                <LoadingButton
                                    color='success'
                                    loadingPosition='start'
                                    startIcon={<img src={DOWNLOAD} alt='' />}
                                    sx={{
                                        '.MuiLoadingButton-label': {
                                            fontSize: 12,
                                            textDecoration: 'underline'
                                        }
                                    }}
                                    loading={isDownloadingAttendanceData}
                                    onClick={handleDownloadAttendanceData}
                                >
                                    Download Now
                                </LoadingButton>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'space-evenly',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'grey.500',
                                height: 210,
                                width: 310
                            }}>
                                <Box sx={{
                                    width: 60,
                                    height: 60,
                                    background: '#E1E8F5',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignContent: 'center'
                                }}>
                                    <img src={QUESTION_TEMPLATE} alt="" />
                                </Box>
                                <Box sx={{
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                }}>
                                    question template
                                </Box>
                                <LoadingButton
                                    color='success'
                                    startIcon={<img src={DOWNLOAD} alt='' />}
                                    loadingPosition='start'
                                    sx={{
                                        '.MuiLoadingButton-label': {
                                            fontSize: 12,
                                            textDecoration: 'underline'
                                        }
                                    }}
                                    loading={isDownloadingQuestionTemplate}
                                    onClick={handleDownloadQuestionTemplate}
                                >
                                    Download Now
                                </LoadingButton>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'space-evenly',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'grey.500',
                                height: 210,
                                width: 310
                            }}>
                                <Box sx={{
                                    width: 60,
                                    height: 60,
                                    background: '#E1E8F5',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignContent: 'center'
                                }}>
                                    <img src={ANSWER_KEY} alt="" />
                                </Box>
                                <Box sx={{
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                }}>
                                    answer key template
                                </Box>
                                <LoadingButton
                                    color='success'
                                    loadingPosition='start'
                                    startIcon={<img src={UPLOAD} alt='' />}
                                    sx={{
                                        '.MuiLoadingButton-label': {
                                            fontSize: 12,
                                            textDecoration: 'underline'
                                        }
                                    }}
                                    onClick={() => {
                                        if (!selectedProjectMaster) {
                                            addNotification('Please select exam for further processing.')
                                            return;
                                        }
                                        setIsVisible(true);
                                    }}
                                >
                                    Upload Now
                                </LoadingButton>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'space-evenly',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'grey.500',
                                height: 210,
                                width: 310
                            }}>
                                <Box sx={{
                                    width: 60,
                                    height: 60,
                                    background: '#E1E8F5',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignContent: 'center'
                                }}>
                                    <img src={RAW_SCORES} alt="" />
                                </Box>
                                <Box sx={{
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                }}>
                                    raw scores
                                </Box>
                                <LoadingButton
                                    color='success'
                                    loadingPosition='start'
                                    startIcon={<img src={GENERATE} alt='' />}
                                    loading={isGenerating}
                                    sx={{
                                        '.MuiLoadingButton-label': {
                                            fontSize: 12,
                                            textDecoration: 'underline'
                                        }
                                    }}

                                    onClick={handleGenerateRawScore}
                                >
                                    {isGenerated ? 'Generated' : 'Generate Now'}
                                </LoadingButton>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'space-evenly',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'grey.500',
                                height: 210,
                                width: 310
                            }}>
                                <Box sx={{
                                    width: 60,
                                    height: 60,
                                    background: '#E1E8F5',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignContent: 'center'
                                }}>
                                    <img src={RAW_SCORES} alt="" />
                                </Box>
                                <Box sx={{
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                }}>
                                    raw scores
                                </Box>
                                <LoadingButton
                                    color='success'
                                    loadingPosition='start'
                                    startIcon={<img src={DOWNLOAD} alt='' />}
                                    loading={isDownloading}
                                    sx={{
                                        '.MuiLoadingButton-label': {
                                            fontSize: 12,
                                            textDecoration: 'underline'
                                        }
                                    }}

                                    onClick={handleDownloadRawScore}
                                >
                                    Download Now
                                </LoadingButton>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'space-evenly',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'grey.500',
                                height: 210,
                                width: 310
                            }}>
                                <Box sx={{
                                    width: 60,
                                    height: 60,
                                    background: '#E1E8F5',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignContent: 'center'
                                }}>
                                    <img src={REPORT} alt="" />
                                </Box>
                                <Box sx={{
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                }}>
                                    10X report
                                </Box>
                                <LoadingButton
                                    color='success'
                                    loadingPosition='start'
                                    startIcon={<img src={DOWNLOAD} alt='' />}
                                    loading={isDownloadingReport}
                                    sx={{
                                        '.MuiLoadingButton-label': {
                                            fontSize: 12,
                                            textDecoration: 'underline'
                                        }
                                    }}

                                    onClick={handleDownloadReport}
                                >
                                    Download Now
                                </LoadingButton>
                            </Box>

                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'space-evenly',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'grey.500',
                                height: 210,
                                width: 310
                            }}>
                                <Box sx={{
                                    width: 60,
                                    height: 60,
                                    background: '#E1E8F5',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignContent: 'center'
                                }}>
                                    <img src={REPORT} alt="" />
                                </Box>
                                <Box sx={{
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                }}>
                                    Download audit log
                                </Box>
                                <LoadingButton
                                    color='success'
                                    loadingPosition='start'
                                    startIcon={<img src={DOWNLOAD} alt='' />}
                                    loading={isDownloadingAuditLog}
                                    sx={{
                                        '.MuiLoadingButton-label': {
                                            fontSize: 12,
                                            textDecoration: 'underline'
                                        }
                                    }}
                                    onClick={handleOpenRollNoPopup}
                                >
                                    Download Now
                                </LoadingButton>
                            </Box>

                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'space-evenly',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'grey.500',
                                height: 210,
                                width: 310
                            }}>
                                <Box sx={{
                                    width: 60,
                                    height: 60,
                                    background: '#E1E8F5',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignContent: 'center'
                                }}>
                                    <img src={REPORT} alt="" />
                                </Box>
                                <Box sx={{
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                }}>
                                    Feedback report
                                </Box>
                                <LoadingButton
                                    color='success'
                                    loadingPosition='start'
                                    startIcon={<img src={DOWNLOAD} alt='' />}
                                    loading={isDownloadingFeedbackReport}
                                    sx={{
                                        '.MuiLoadingButton-label': {
                                            fontSize: 12,
                                            textDecoration: 'underline'
                                        }
                                    }}

                                    onClick={handleDownloadFeedbackReport}
                                >
                                    Download Now
                                </LoadingButton>
                            </Box>

                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'space-evenly',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'grey.500',
                                height: 210,
                                width: 310
                            }}>
                                <Box sx={{
                                    width: 60,
                                    height: 60,
                                    background: '#E1E8F5',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignContent: 'center'
                                }}>
                                    <img src={REPORT} alt="" />
                                </Box>
                                <Box sx={{
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                }}>
                                    Answer Paper Download
                                </Box>
                                <LoadingButton
                                    color='success'
                                    loadingPosition='start'
                                    startIcon={<img src={DOWNLOAD} alt='' />}
                                    sx={{
                                        '.MuiLoadingButton-label': {
                                            fontSize: 12,
                                            textDecoration: 'underline'
                                        }
                                    }}
                                    loading={false}
                                    onClick={openAnswerPaperDownloadPopup}
                                >
                                    Download Now
                                </LoadingButton>
                            </Box>


                        </Box>
                    </Box>


                </Box>


                {isVisible && <UpdateAnswerKeyModal projectMasterId={selectedProjectMaster} isVisible={isVisible} setIsVisible={setIsVisible} />}

                <Modal open={auditLogModalVisible} className='backdrop' hideBackdrop={true} onClose={handleCloseRollNoPopup}>
                    <Box className='modal-container' sx={{
                        maxWidth: 480,
                        px: 5,
                        py: 2.5
                    }}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                        }} component="form"
                            autoComplete="off"
                            onSubmit={handleSubmit(handleDownloadAuditLog)}
                        >
                            <Typography variant='h6' sx={{
                                fontWeight: 400,
                                mb: 1,
                            }}>Enter roll no</Typography>
                            <TextField
                                sx={{ mb: 3 }}
                                variant="outlined"
                                label='Roll No*'
                                className='text-input'
                                id='rollNo'
                                margin='normal'
                                fullWidth name='rollNo'
                                {...register('rollNo')}
                                error={Boolean(rollNoErrors.rollNo)}
                                helperText={rollNoErrors?.rollNo?.message}
                                onBlur={() => rollNoTrigger('rollNo')}
                            />
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'flex-end',
                                justifyContent: 'flex-end',
                                gap: 2
                            }}>
                                <RadiusButton variant='outlined' onClick={handleCloseRollNoPopup}>
                                    Cancel
                                </RadiusButton>
                                <LoadingButton
                                    type='submit'
                                    sx={{
                                        borderRadius: 40,
                                        textTransform: 'capitalize'
                                    }}
                                    variant='contained'
                                    disabled={isDownloadingAuditLog || !!rollNoErrors?.rollNo?.message}
                                    loading={isDownloadingAuditLog}
                                >
                                    Download
                                </LoadingButton>
                            </Box>
                        </Box>
                    </Box>
                </Modal>

                <Modal open={answerPaperDownloadPopup.isOpen} className='backdrop' hideBackdrop={true} onClose={closeAnswerPaperDownloadPopup}>
                    <Box className='modal-container' sx={{
                        maxWidth: 580,
                    }}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                        }} component="form"
                            autoComplete="off"
                            onSubmit={answerPaperDownloadHandleSubmit(answerPaperDownloadSubmit)}
                        >
                            <Tabs value={answerPaperDownloadPopup.selectedTab} onChange={(e, v) => {
                                setAnswerPaperDownloadPopup({
                                    ...answerPaperDownloadPopup,
                                    selectedTab: v
                                });
                            }} aria-label="basic tabs example">
                                <Tab label="Download" {...a11yProps(0)} />
                                <Tab label="Export To Google Storage" {...a11yProps(1)} />
                            </Tabs>

                            <CustomTabPanel value={answerPaperDownloadPopup.selectedTab} index={0}>
                                <Typography variant='h6' sx={{
                                    fontWeight: 400,
                                    // mb: 1,
                                }}>Enter roll no(s)</Typography>
                                <TextField
                                    variant="outlined"
                                    label='Roll No*'
                                    className='text-input'
                                    id='rollNo'
                                    margin='normal'
                                    fullWidth name='rollNo'
                                    {...answerPaperDownloadRegister('rollNo')}
                                    error={Boolean(answerPaperDownloadErrors.rollNo)}
                                    helperText={answerPaperDownloadErrors?.rollNo?.message}
                                    onBlur={() => answerPaperDownloadTrigger('rollNo')}
                                />
                                <FormHelperText sx={{ color: 'primary.main', mb: 3 }}>
                                    Please enter comma separated roll numbers to download answer papers. E.g.- 101,102,103
                                </FormHelperText>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                    justifyContent: 'flex-end',
                                    gap: 2
                                }}>
                                    <RadiusButton variant='outlined' onClick={closeAnswerPaperDownloadPopup}>
                                        Cancel
                                    </RadiusButton>
                                    <LoadingButton
                                        type='submit'
                                        sx={{
                                            borderRadius: 40,
                                            textTransform: 'capitalize'
                                        }}
                                        variant='contained'
                                        disabled={answerPaperDownloading || !!answerPaperDownloadErrors?.rollNo?.message}
                                        loading={answerPaperDownloading}
                                    >
                                        Download
                                    </LoadingButton>
                                </Box>
                            </CustomTabPanel>
                            <CustomTabPanel value={answerPaperDownloadPopup.selectedTab} index={1}>
                                <Typography variant='h6' sx={{ fontWeight: 400 }}>Enter roll no(s)</Typography>
                                <TextField
                                    variant="outlined"
                                    label='Roll No*'
                                    className='text-input'
                                    id='rollNo'
                                    margin='normal'
                                    fullWidth name='rollNo'
                                    {...answerPaperDownloadRegister('rollNo')}
                                    error={Boolean(answerPaperDownloadErrors.rollNo)}
                                    helperText={answerPaperDownloadErrors?.rollNo?.message}
                                    onBlur={() => answerPaperDownloadTrigger('rollNo')}
                                />
                                <FormHelperText sx={{ color: 'primary.main', mb: 1 }}>
                                    Enter comma separated roll numbers to download answer papers. E.g.- 101,102,103
                                </FormHelperText>
                                <TextField
                                    variant="outlined"
                                    label='Drive Path*'
                                    className='text-input'
                                    id='drivePath'
                                    margin='normal'
                                    fullWidth name='drivePath'
                                    {...answerPaperDownloadRegister('drivePath')}
                                    error={Boolean(answerPaperDownloadErrors.drivePath)}
                                    helperText={answerPaperDownloadErrors?.drivePath?.message}
                                    onBlur={() => answerPaperDownloadTrigger('drivePath')}
                                />
                                <FormHelperText sx={{ color: 'primary.main', mb: 3 }}>
                                    Enter full path for storing answers. E.g.- /path/to/answers
                                </FormHelperText>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                    justifyContent: 'flex-end',
                                    gap: 2
                                }}>
                                    <RadiusButton variant='outlined' onClick={closeAnswerPaperDownloadPopup}>
                                        Cancel
                                    </RadiusButton>
                                    <LoadingButton
                                        type='submit'
                                        sx={{
                                            borderRadius: 40,
                                            textTransform: 'capitalize'
                                        }}
                                        variant='contained'
                                        disabled={answerPaperDownloading || !!answerPaperDownloadErrors?.rollNo?.message || !!answerPaperDownloadErrors?.drivePath?.message}
                                        loading={answerPaperDownloading}
                                    >
                                        Export
                                    </LoadingButton>
                                </Box>
                            </CustomTabPanel>
                        </Box>
                    </Box>
                </Modal>
            </Container>

        </MainContainer>
    );
}
